<script setup lang="ts">
import { computed, ref, onUpdated, onMounted } from "vue";
import { onClickOutside } from "@vueuse/core";

import type { iconName } from "@/Modules/App/Models/Styles/Icons";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

export type anyValue = string | number | boolean | unknown;

export interface Option<T = anyValue, K = anyValue> {
  icon?: iconName;
  class?: string;
  label: string;
  value: T;
  extraValue?: K; //Optional prop for additional information
}

export interface TcSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: Option[];
  modelValue?: Option;
  isDisabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  maxItemsScroll?: number;
  gutterBottom?: boolean;
  isSmall?: boolean;
}

const ITEM_HEIGHT = 56;

const props = withDefaults(defineProps<TcSelectProps>(), {
  maxItemsScroll: 5,
  gutterBottom: true,
  isSmall: false,
});

const emit = defineEmits<{
  (event: "change", option: Option): void;
  (event: "update:modelValue", option: Option): void;
}>();

const visible = ref<boolean>(false);
const selected = ref<Option | undefined>(undefined);
const selectRef = ref<HTMLDivElement | null>(null);

const maxHeight = computed(() => props.maxItemsScroll * ITEM_HEIGHT);

const toggle = () => {
  if (!props.isDisabled) {
    visible.value = !visible.value;
  }
};

const select = (option: Option) => {
  emit("change", option);
  emit("update:modelValue", option);

  selected.value = option;
};

const onClickOut = () => {
  if (visible.value) {
    visible.value = false;
  }
};

onMounted(() => {
  if (hasValue(props.modelValue) && Array.isArray(props.options)) {
    const candidate = props.options.find((op) => op.value === props.modelValue?.value);

    if (hasValue(candidate)) {
      selected.value = candidate;
    }
  }
});

onUpdated(() => {
  if (hasValue(selected.value) && props.options.findIndex((o) => o.value === selected.value?.value) === -1) {
    selected.value = undefined;
  }

  if (
    hasValue(props.modelValue) &&
    (!hasValue(selected.value) || props.modelValue.value !== selected.value)
  ) {
    const candidate = props.options.find((op) => op.value === props.modelValue?.value);

    if (hasValue(candidate)) {
      selected.value = candidate;
    }
  }
});

onClickOutside(selectRef, onClickOut);
</script>

<template>
  <div
    ref="selectRef"
    class="tc-select"
    :class="{
      'tc-select--disabled': props.isDisabled,
      'mb-4': props.gutterBottom,
      'tc-select--small': props.isSmall,
    }"
  >
    <label v-if="isValidString(props.label)" class="tc-select__label">
      {{ props.label }}
    </label>

    <div
      class="tc-select__selector"
      :class="{
        'tc-select__selector--error': props.error,
        'tc-select__selector--visible': visible,
        'tc-select__selector--expanded': visible,
      }"
      @click="toggle"
    >
      <div v-if="!hasValue(selected)" class="tc-select__selector__placeholder">
        <span v-if="isValidString(props.placeholder)">{{ props.placeholder }}</span>
        <span v-else>&nbsp;</span>
      </div>

      <div v-if="hasValue(selected)" class="tc-select__selector__value">
        <span>{{ selected.label }}</span>
      </div>

      <TcIcon name="ChevronDown" class="tc-select__selector__arrow" />

      <template v-if="visible">
        <ul class="tc-select__selector__list" :style="'max-height:' + maxHeight + 'px'">
          <li
            v-for="(option, index) in props.options"
            class="tc-select__selector__list__element"
            :key="`selector-${props.name}-${index}`"
            :class="{
              'tc-select__selector__list__element--current':
                hasValue(selected) && option.value === selected.value,
              ...(isValidString(option.class) ? { [option.class]: true } : {}),
            }"
            @click="select(option)"
          >
            <div>
              <TcIcon
                v-if="isValidString(option.icon)"
                class="tc-select__selector__list__element__icon"
                color="content-high"
                size="sm"
                :name="option.icon as iconName"
              />

              <p class="tc-select__selector__list__element__label">
                {{ option.label }}
              </p>
            </div>
          </li>
        </ul>
      </template>
    </div>

    <span v-if="props.error" class="tc-select__error">{{ props.errorMessage }}</span>
  </div>
</template>
<style scoped lang="scss">
.tc-select {
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &__label {
    @include text.general(low, m);
    @include colors.color(content, high);
    @include sp.margin(b, 2);

    display: flex;
  }

  &__error {
    @include text.general(low, s);
    @include colors.color(semantic, error);
  }

  &__selector {
    @include radius(s);
    @include border(1, solid);
    @include colors.border(content, low);

    position: relative;
    display: flex;
    width: 100%;

    &__arrow {
      position: absolute;
      right: $space-4;
      top: 32%;
      transform: rotateZ(0deg) translateY(0px);
      transition-duration: 0.3s;
      transition-timing-function: cubic-bezier(0.59, 1.39, 0.37, 1.01);
    }

    &__placeholder,
    &__value {
      @include text.general(low, l);
      @include sp.padding(y, 3);
      @include sp.padding(l, 4);
      @include sp.padding(r, 12);

      display: flex;
      width: 100%;
    }

    &__value {
      @include colors.color(content, high);
    }

    &__placeholder {
      @include colors.color(content, mid);
    }

    &__list {
      @include elevation(m);
      @include sp.margin(y, 1);
      @include colors.bg(background, base);
      @include sp.padding(false, 0);

      width: calc(100% + 2px);
      list-style-type: none;
      position: absolute;
      left: -1px; // es para que si tiene un input detrás las opciones no se vea el bordeado
      top: 100%;
      // fallback if it's a max-height computed error
      max-height: 256px;
      overflow-y: scroll;
      z-index: 1001;

      &::-webkit-scrollbar {
        width: 5px;
        height: 43px;

        &-track {
          @include colors.border(background, transparent);
        }

        &-thumb {
          @include colors.bg(content, low);
        }
      }

      &__element {
        @include text.general(low, l);
        @include colors.color(content, high);
        @include sp.padding(l, 4);

        & > div {
          @include border(1, solid, b);
          @include colors.border(content, low);

          display: flex;
          align-items: center;
        }

        &__icon {
          @include sp.margin(r, 2);

          flex-grow: 0;
        }

        &__label {
          @include text.general(low, l);
          @include colors.color(content, high);
          @include sp.padding(y, 4);

          flex-grow: 1;
        }

        &:last-child {
          & div {
            border-bottom: 0px !important;
          }
        }

        &:hover {
          @include colors.bg(background, alt);
        }

        &--current {
          @include colors.bg(background, alt);
        }
      }
    }

    &--visible {
      @include border(1, solid);
      @include colors.border(content, high);
    }

    &--error {
      @include border(1, solid);
      @include colors.border(semantic, error);
    }

    &--expanded {
      .tc-select__selector__arrow {
        transform: rotateZ(180deg) translateY(2px);
      }
    }
  }

  &--small {
    .tc-select__selector__value {
      @include sp.padding(y, 2, true);
    }
  }

  &--disabled {
    opacity: 40%;
    cursor: not-allowed;
    pointer-events: none;
  }

  .hidden {
    visibility: hidden;
  }

  .visible {
    visibility: visible;
  }
}
</style>
